import api from "./client";

/* SOPs — agent_actions TenantSOP (lane D owns the store). Versioned markdown
   documents keyed by slug; one current version per slug; only PUBLISHED current
   SOPs are injected into the assistant's context.

   Read: any authenticated staff. Author/publish/send: admin/manager (server-gated).
   Acknowledgments are lane D's hr_app.SOPAcknowledgment (version-pinned, on the
   reader's EmploymentProfile) — the WRITE is D's /api/people/ endpoint; the
   coverage read (sent + acknowledged, merged) is /api/agent/sops/<slug>/acknowledgments/. */

export const listSops = () => api.get("/agent/sops/");
export const getSop = (slug) => api.get(`/agent/sops/${encodeURIComponent(slug)}/`);
export const createSop = (body) => api.post("/agent/sops/", body);
// POST to the slug appends a new version (carry-forward for omitted fields).
export const updateSop = (slug, body) => api.post(`/agent/sops/${encodeURIComponent(slug)}/`, body);
export const setPublished = (slug, is_published) => updateSop(slug, { is_published });

// Full version chain (newest first), bodies included so the client can diff.
export const listSopVersions = (slug) => api.get(`/agent/sops/${encodeURIComponent(slug)}/versions/`);

// Send "read and acknowledge" — published SOPs only (409 on a draft).
// { user_ids?: number[], group?: 'all'|'field'|'office', channels: ('email'|'push')[], note?: string }
export const sendSop = (slug, body) => api.post(`/agent/sops/${encodeURIComponent(slug)}/send/`, body);

// Who has read it: every EmploymentProfile with sent/acknowledged state for the current version.
export const getSopAcknowledgments = (slug) => api.get(`/agent/sops/${encodeURIComponent(slug)}/acknowledgments/`);
// The caller acknowledges the CURRENT PUBLISHED version (D's endpoint; 404 on a draft,
// 400 not_an_employee when the caller has no employment profile).
export const acknowledgeSop = (slug) => api.post(`/people/sops/${encodeURIComponent(slug)}/acknowledge/`, {});

// Tenant-branded PDF of a PUBLISHED version, stored in the document library:
// { file_uuid, filename, url (presigned, opens on a phone), branding:{logo,name} }.
// 409 draft_not_stored for a draft → fall back to downloadSopPdf.
export const getSopPdfLink = (slug) => api.get(`/agent/sops/${encodeURIComponent(slug)}/pdf/`, { params: { url: 1 } });

// Print-clean PDF for the binder. Blob → object URL (the API needs the auth header, so no plain link).
export const downloadSopPdf = async (slug, filename) => {
  const res = await api.get(`/agent/sops/${encodeURIComponent(slug)}/pdf/`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = filename || `${slug}.pdf`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};

// Review conversation — lane B's loop (crm_backend docs/SOP_REVIEW_CONTRACT.md).
// GET  review/          → { version, is_published, body, open_questions, next_question, remaining, turns[] }
// POST revise/ {message} (alias of review/) → { version, is_published, was_published, summary, diff,
//                                              answered[], remaining[], next_question, turn }
//      502 redraft_unavailable = nothing written; 403 not admin/manager.
// POST review/approve/  → publishes the current version (approve is the confirm).
export const getSopReview = (slug) => api.get(`/agent/sops/${encodeURIComponent(slug)}/review/`);
export const reviseSop = (slug, body) => api.post(`/agent/sops/${encodeURIComponent(slug)}/revise/`, body);
export const approveSopReview = (slug) => api.post(`/agent/sops/${encodeURIComponent(slug)}/review/approve/`, {});

// The AI SOP maker: a title becomes a real first draft (crm_backend
// docs/SOP_MAKER_CONTRACT.md) — steps carry their source, "(suggested —
// standard practice, confirm)" marks the ones the tenant's records don't show.
export const draftSop = ({ title, category = "general", notes = "" }) =>
  api.post("/agent/sops/draft/", { title, category, notes });

// Knowledge coverage: what the assistant can answer about this tenant and where
// the gaps are. Deterministic and read-only on the backend, safe on page load.
export const getKnowledgeCoverage = () => api.get("/agent/knowledge/coverage/");

/* Ask a question about ONE procedure (agent_actions/sop_qa). The assistant's
   sop.ask tool shares this retrieval, so the panel and a tech in the field get
   the same answer from the same document. The endpoint owns the three rules —
   scoped to this SOP, cited, and grounded (it says when the document does not
   settle it rather than inventing) — the client's job is not to soften any of
   them. GET returns what the document covers. */
export const askSop = (slug, question) =>
  api.post(`/agent/sops/${encodeURIComponent(slug)}/ask/`, { question });
export const getSopCoverage = (slug) => api.get(`/agent/sops/${encodeURIComponent(slug)}/ask/`);
